import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DTypeFormChipListField } from '@grupo-constant/next-dtype/form';
import { DTypeCol, DTypeViewableCol, FILE_COLUMN_URI, FORMATTEDTEXT_COLUMN_URI, IMAGE_COLUMN_URI, MULTIIMAGE_COLUMN_URI, RATING_COLUMN_URI, SECTION_COLUMN_URI, SMILEYS_COLUMN_URI, SUBSECTION_COLUMN_URI, TEXTAREA_COLUMN_URI, TWOCOLUMNSTABLE_URI } from '@grupo-constant/next-dtype/types';
import { debounceTime, startWith, Subject, takeUntil } from 'rxjs';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { DTypeService } from '../../dtype.service';
import { LocalizePipe } from '../../../pipes/standalone/localize.pipe';
import { Utils } from '../../../utils/utils';


@Component({
    selector: 'field-chiplist-field',
    templateUrl: 'field-chiplist-field.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, LocalizePipe, MatAutocompleteModule, MatChipsModule, MatFormFieldModule, ReactiveFormsModule]
})
export class DtypeFieldChiplistFieldComponent implements DTypeFormFieldComponent, OnInit, OnDestroy {

    @Input() field: DTypeFormChipListField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    searchCtrl = new FormControl('');
    options: DTypeCol[] = [];

    columns: DTypeCol[];
    invalidTypes = [
        TEXTAREA_COLUMN_URI,
        IMAGE_COLUMN_URI,
        MULTIIMAGE_COLUMN_URI,
        FILE_COLUMN_URI,
        SECTION_COLUMN_URI,
        SUBSECTION_COLUMN_URI,
        FORMATTEDTEXT_COLUMN_URI,
        TWOCOLUMNSTABLE_URI,
        SMILEYS_COLUMN_URI,
        RATING_COLUMN_URI
    ];

    readonly separators: number[] = [ENTER, COMMA];

    private _unsubscribeAll = new Subject<void>();

    constructor(private dtypeService: DTypeService) { }

    ngOnInit() {
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }

        this.dtypeService.columns$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((columns) => {
                this.columns = columns.filter(c => this.columnIsValid(c));
                const filterValue = this.searchCtrl.value;
                if (typeof filterValue === 'string' && filterValue.length >= 0) {
                    this.options = this.columns.filter(col => Utils.removeAccents(col.name).toLowerCase().includes(Utils.removeAccents(filterValue).toLowerCase()));
                } else {
                    this.options = this.columns;
                }
            });

        // TODO process when field.control is enabled/disabled
        if (this.field.control.disabled) {
            this.searchCtrl.disable();
        }

        this.searchCtrl.valueChanges.pipe(
            takeUntil(this._unsubscribeAll),
            startWith(''),
            debounceTime(500)
        ).subscribe((value) => {
            if (typeof value === 'string' && value.length >= 0) {
                this.options = this.columns.filter(col => Utils.removeAccents(col.name).toLowerCase().includes(Utils.removeAccents(value).toLowerCase()));
                return;
            }
            this.options = this.columns;
        });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    removeChip(index: number) {
        const value = this.field.control.value;
        value.splice(index, 1);
        this.field.control.setValue(value);
    }

    add(event: MatChipInputEvent) {
        const value = (event.value || '').trim();

        if (value) {
            const option = this.options.find(o => Utils.removeAccents(o.name).toLowerCase() === Utils.removeAccents(value).toLowerCase());
            if (option) {
                const selectedOptions = this.field.control.value;
                selectedOptions.push(option);
                this.field.control.setValue(selectedOptions);
            }
        }

        event.chipInput.clear();
        this.searchCtrl.setValue(null);
    }

    selected(event: MatAutocompleteSelectedEvent) {
        if (!this.field.control.value.find(v => typeof v === 'string' ? v === event.option.value.name :
            v.name === event.option.value.name)) {
            const value = this.field.control.value;
            value.push(event.option.value);
            this.field.control.setValue(value);
        }
        this.searchCtrl.setValue('');
    }

    displayFn(option: DTypeCol) {
        return option && option.name ? option.name : '';
    }

    private columnIsValid(column: DTypeCol) {
        return !this.invalidTypes.includes(column.type);
    }

}
